import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { TransactionStatsCards, PendingPaymentsSection } from '../../components/transactions/TransactionWidgets';
import { toast } from 'sonner';
import { 
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const AdminTransactions = () => {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0, revenue: 0 });
  const [zellePending, setZellePending] = useState([]);
  const [predPending, setPredPending] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);

  const fetchZellePending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/admin/zelle/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZellePending(res.data.payments || []);
    } catch (e) {
      console.warn('Error fetching Zelle pending:', e.message);
    }
  }, [token]);

  const fetchPredPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/prediction-payments/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPredPending(res.data.payments || []);
    } catch (e) {
      console.warn('Error fetching prediction pending:', e.message);
    }
  }, [token]);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(res.data.transactions || []);
      
      const txs = res.data.transactions || [];
      const completed = txs.filter(t => t.payment_status === 'paid' || t.payment_status === 'complete');
      const pending = txs.filter(t => t.payment_status === 'pending' || t.payment_status === 'pending_verification');
      const revenue = completed.reduce((sum, t) => sum + (t.amount || 0), 0);
      
      setStats({
        total: txs.length,
        completed: completed.length,
        pending: pending.length,
        revenue: revenue
      });
    } catch (e) {
      toast.error('Error al cargar transacciones');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTransactions();
    fetchZellePending();
    fetchPredPending();
  }, [fetchTransactions, fetchZellePending, fetchPredPending]);

  const handleZelleReview = async (transactionId, action, type = 'subscription') => {
    setReviewingId(transactionId);
    const endpoint = type === 'prediction'
      ? `${API}/admin/prediction-payments/review`
      : `${API}/subscription/admin/zelle/review`;
    try {
      const res = await axios.post(
        endpoint,
        { transaction_id: transactionId, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchZellePending();
      fetchPredPending();
      fetchTransactions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar');
    } finally {
      setReviewingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      complete: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      pending_verification: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    
    const icons = {
      paid: <CheckCircle2 size={12} className="mr-1" />,
      complete: <CheckCircle2 size={12} className="mr-1" />,
      pending: <Clock size={12} className="mr-1" />,
      pending_verification: <Clock size={12} className="mr-1" />,
      failed: <XCircle size={12} className="mr-1" />,
      rejected: <XCircle size={12} className="mr-1" />,
      cancelled: <XCircle size={12} className="mr-1" />
    };
    
    const labels = {
      pending_verification: 'POR VERIFICAR',
      rejected: 'RECHAZADO'
    };
    
    return (
      <Badge className={styles[status] || styles.pending}>
        {icons[status] || icons.pending}
        {labels[status] || status?.toUpperCase()}
      </Badge>
    );
  };

  const getPlanBadge = (planId) => {
    const colors = {
      vip: 'bg-amber-500/20 text-amber-400',
      premium: 'bg-purple-500/20 text-purple-400',
      basic: 'bg-blue-500/20 text-blue-400'
    };
    return <Badge className={colors[planId] || colors.basic}>{planId?.toUpperCase()}</Badge>;
  };

  const filteredTransactions = useMemo(() => transactions.filter(tx => {
    const matchesSearch = tx.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tx.session_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || tx.payment_status === filterStatus;
    return matchesSearch && matchesStatus;
  }), [transactions, searchTerm, filterStatus]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Transacciones" subtitle="Cargando...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-amber-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Transacciones" subtitle="Historial de pagos y suscripciones">
      <TransactionStatsCards stats={stats} />

      <PendingPaymentsSection
        zellePending={zellePending}
        predPending={predPending}
        onReview={handleZelleReview}
        reviewingId={reviewingId}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            placeholder="Buscar por email o ID de sesión..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-gray-900 border-gray-700 text-white"
            data-testid="transactions-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-gray-900 border-gray-700 text-white" data-testid="transactions-status-filter">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all" className="text-white hover:bg-gray-800">Todos</SelectItem>
            <SelectItem value="paid" className="text-white hover:bg-gray-800">Pagado</SelectItem>
            <SelectItem value="pending" className="text-white hover:bg-gray-800">Pendiente</SelectItem>
            <SelectItem value="failed" className="text-white hover:bg-gray-800">Fallido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions Table */}
      <Card className="bg-gray-900 border-gray-800" data-testid="transactions-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400">Usuario</TableHead>
                <TableHead className="text-gray-400">Plan</TableHead>
                <TableHead className="text-gray-400">Monto</TableHead>
                <TableHead className="text-gray-400">Metodo</TableHead>
                <TableHead className="text-gray-400">Estado</TableHead>
                <TableHead className="text-gray-400">Ciclo</TableHead>
                <TableHead className="text-gray-400">Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                    No hay transacciones que mostrar
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map((tx, idx) => (
                  <TableRow key={tx.id || idx} className="border-gray-800 hover:bg-gray-800/50">
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{tx.user_email || 'Usuario'}</p>
                        <p className="text-gray-500 text-xs font-mono">{tx.session_id?.slice(0, 20)}...</p>
                      </div>
                    </TableCell>
                    <TableCell>{getPlanBadge(tx.plan_id)}</TableCell>
                    <TableCell>
                      <span className="text-white font-bold">${(tx.amount || 0).toFixed(2)}</span>
                      <span className="text-gray-500 text-xs ml-1">{tx.currency?.toUpperCase()}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={tx.payment_method === 'zelle' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}>
                        {tx.payment_method === 'zelle' ? 'Zelle' : tx.payment_method === 'cash_app' ? 'Cash App' : tx.payment_method === 'paypal' ? 'PayPal' : 'Tarjeta'}
                      </Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(tx.payment_status)}</TableCell>
                    <TableCell className="text-gray-400">{tx.billing_cycle || 'monthly'}</TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {formatDate(tx.created_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminTransactions;
